import type { TenantWindow } from '../../../../api/organization/allocationWindowTypes';

/**
 * Layout of the season plan (M11): a week of weekday columns with each
 * window as a block. Pure, so the lanes are tested without a screen.
 */

export const minutesOf = (time: string): number => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
};

export interface PlacedWindow {
    window: TenantWindow;
    /** Column lane within its weekday, 0-based, and how many lanes that weekday needs. */
    lane: number;
    lanes: number;
}

/**
 * Windows of one weekday that share time (another season, or another
 * organisation in another season) sit side by side instead of on top of
 * each other. Greedy lanes in start order.
 */
export const placeWindows = (windows: TenantWindow[]): PlacedWindow[] => {
    const placed: PlacedWindow[] = [];
    for (let weekday = 1; weekday <= 7; weekday += 1) {
        const day = windows
            .filter((w) => w.weekday === weekday)
            .sort((a, b) => minutesOf(a.startTime) - minutesOf(b.startTime) || minutesOf(a.endTime) - minutesOf(b.endTime));
        const laneEnds: number[] = [];
        const lanesOf: number[] = [];
        for (const w of day) {
            const start = minutesOf(w.startTime);
            let lane = laneEnds.findIndex((end) => end <= start);
            if (lane === -1) {
                lane = laneEnds.length;
                laneEnds.push(0);
            }
            laneEnds[lane] = minutesOf(w.endTime);
            lanesOf.push(lane);
        }
        const lanes = Math.max(1, laneEnds.length);
        day.forEach((w, i) => placed.push({ window: w, lane: lanesOf[i], lanes }));
    }
    return placed;
};

/** The hours the plan shows: from the earliest start to the latest end, whole hours, at least 8–22. */
export const hourRange = (windows: TenantWindow[]): { from: number, to: number } => {
    let from = 8;
    let to = 22;
    for (const w of windows) {
        from = Math.min(from, Math.floor(minutesOf(w.startTime) / 60));
        to = Math.max(to, Math.ceil(minutesOf(w.endTime) / 60));
    }
    return { from, to };
};
